// Offline-first queue for temperature checks using IndexedDB. Never lose a record.
import { api } from "./api";

const DB_NAME = "tempcontrol";
const STORE = "pending_checks";
const PSTORE = "pending_planillas";
const DB_VERSION = 2;

function createStores(db) {
  if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "local_id" });
  if (!db.objectStoreNames.contains(PSTORE)) db.createObjectStore(PSTORE, { keyPath: "client_id" });
}

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => createStores(req.result);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const res = fn(store);
    t.oncomplete = () => resolve(res);
    t.onerror = () => reject(t.error);
  });
}

export async function queueCheck(payload) {
  const local_id = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const record = { local_id, payload: { ...payload, offline_created: true, client_timestamp: new Date().toISOString() }, created_at: new Date().toISOString() };
  await tx("readwrite", (s) => s.put(record));
  return record;
}

export async function getPending() {
  return tx("readonly", (s) => new Promise((resolve) => {
    const req = s.getAll();
    req.onsuccess = () => resolve(req.result || []);
  })).then((r) => r);
}

export async function pendingCount() {
  const all = await listPending();
  return all.length;
}

async function listPending() {
  const db = await openDB();
  return new Promise((resolve) => {
    const t = db.transaction(STORE, "readonly");
    const req = t.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });
}

async function removePending(local_id) {
  await tx("readwrite", (s) => s.delete(local_id));
}

let syncing = false;
export async function syncPending(onResult) {
  if (syncing || !navigator.onLine) return { synced: 0 };
  syncing = true;
  let synced = 0;
  try {
    const all = await listPending();
    for (const rec of all) {
      try {
        const res = await api.post("/temperature-checks", rec.payload);
        await removePending(rec.local_id);
        synced++;
        if (onResult) onResult(res.data);
      } catch (e) {
        if (e?.response && e.response.status >= 400 && e.response.status < 500) {
          // permanent error -> drop to avoid infinite loop
          await removePending(rec.local_id);
        }
      }
    }
  } finally {
    syncing = false;
  }
  return { synced };
}

export function onConnectivityChange(cb) {
  const handler = () => cb(navigator.onLine);
  window.addEventListener("online", handler);
  window.addEventListener("offline", handler);
  return () => {
    window.removeEventListener("online", handler);
    window.removeEventListener("offline", handler);
  };
}

// ---- Planillas offline queue (second store in same DB) ----
function openPDB() { return openDB(); }
export async function queuePlanilla(payload) {
  const client_id = `pl-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const db = await openPDB();
  await new Promise((res) => { const t = db.transaction(PSTORE, "readwrite"); t.objectStore(PSTORE).put({ client_id, payload: { ...payload, client_id, offline_created: true } }); t.oncomplete = res; });
  return client_id;
}
export async function planillaPendingCount() {
  const db = await openPDB();
  return new Promise((res) => { const t = db.transaction(PSTORE, "readonly"); const r = t.objectStore(PSTORE).getAll(); r.onsuccess = () => res((r.result || []).length); r.onerror = () => res(0); });
}
export async function syncPlanillas() {
  if (!navigator.onLine) return { synced: 0 };
  const db = await openPDB();
  const all = await new Promise((res) => { const t = db.transaction(PSTORE, "readonly"); const r = t.objectStore(PSTORE).getAll(); r.onsuccess = () => res(r.result || []); r.onerror = () => res([]); });
  let synced = 0;
  for (const rec of all) {
    try {
      await api.post("/planillas/submissions", rec.payload);
      const d2 = await openPDB();
      await new Promise((res) => { const t = d2.transaction(PSTORE, "readwrite"); t.objectStore(PSTORE).delete(rec.client_id); t.oncomplete = res; });
      synced++;
    } catch (e) {
      if (e?.response && e.response.status >= 400 && e.response.status < 500) {
        const d2 = await openPDB();
        await new Promise((res) => { const t = d2.transaction(PSTORE, "readwrite"); t.objectStore(PSTORE).delete(rec.client_id); t.oncomplete = res; });
      }
    }
  }
  return { synced };
}
